import React from 'react';
import { getPartner } from '../../../../../../lib/access.es6';

/**
 * Unified component to display policy information across all amendment pages
 *
 * @param {Object} policy - Policy object containing quotation and details
 * @param {Boolean} showTotalPremium - Whether to display the total premium (default: false)
 * @param {Number} totalPremium - Total premium in cents (optional)
 */
const PolicyInfoHeader = ({ policy, showTotalPremium = false, totalPremium = null }) => {
  if (!policy) return null;

  const currencyCode = getPartner().country.currency_code;

  // Extract policy information
  const policyNumber = policy.policy_number || 'N/A';
  const status = policy.status ? policy.status.replace(/_/g, ' ') : 'Unknown';

  // Get coverage type
  const coverageType = policy.quotation && policy.quotation.additional_details
    ? policy.quotation.additional_details.cover_type
    : 'N/A';

  // Get plan
  const primaryPlan = policy.quotation && policy.quotation.additional_details
    ? policy.quotation.additional_details.primary_plan
    : 'N/A';

  // Determine status color
  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('full') || statusLower.includes('active')) {
      return '#28a745'; // Green
    }
    if (statusLower.includes('un confirmed') || statusLower.includes('pending')) {
      return '#ffc107'; // Yellow/Orange
    }
    if (statusLower.includes('cancelled') || statusLower.includes('lapsed')) {
      return '#dc3545'; // Red
    }
    return '#666'; // Default gray
  };

  // Calculate total premium if needed
  const displayPremium = totalPremium !== null
    ? totalPremium
    : (policy.total_premium_amount_in_cents || 0);

  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      padding: '16px 20px',
      marginBottom: '20px'
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: showTotalPremium ? 'repeat(auto-fit, minmax(200px, 1fr))' : 'repeat(4, 1fr)',
        gap: '20px',
        alignItems: 'center'
      }}>
        {/* Policy Number */}
        <div>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px'
          }}>
            Policy Number
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#032A37'
          }}>
            {policyNumber}
          </div>
        </div>

        {/* Status */}
        <div>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px'
          }}>
            Status
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: getStatusColor(status)
          }}>
            {status}
          </div>
        </div>

        {/* Coverage Type */}
        <div>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px'
          }}>
            Coverage Type
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#032A37'
          }}>
            {coverageType}
          </div>
        </div>

        {/* Plan */}
        <div>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px'
          }}>
            Plan
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#032A37'
          }}>
            {primaryPlan}
          </div>
        </div>

        {/* Total Premium (Optional) */}
        {showTotalPremium && (
          <div>
            <div style={{
              fontSize: '11px',
              fontWeight: '600',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px'
            }}>
              Total Premium
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#032A37'
            }}>
              {currencyCode} {(displayPremium / 100).toFixed(2)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyInfoHeader;
